import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";

/**
 * BottleDeconstruction — a 4-act SVG sequence that visualises the geometric
 * origins of the NOOR vessel before the photographic render appears.
 *
 *   Act I  (0.0–0.6s) : a single rose-gold parametric curve traces left→right.
 *   Act II (0.6–1.4s) : the bottle wireframe silhouette draws stroke-by-stroke.
 *   Act III(1.4–2.4s) : the filigree swirl blooms over the lattice.
 *   Act IV (2.4–3.2s) : the entire deconstruction fades, handing off to the photo.
 *
 * The component renders absolutely positioned over the bottle column and removes
 * itself from the DOM after the sequence completes so it never interferes with
 * subsequent interactions or the scroll-driven bottle rotation.
 */

const ROSE = "var(--noor-rosegold, #C9A47A)";
const ROSE_FAINT = "rgba(201,164,122,0.32)";
const EASE = [0.22, 1, 0.36, 1];
const SEQUENCE_MS = 3300;

// --- Path data ---------------------------------------------------------------
// Bottle silhouette — crown stopper + collar + bulbous body + base, stylised
// after the actual NOOR vessel (jeweled dome cap, hourglass body, rose-gold plinth).
const SILHOUETTE_PATHS = [
  "M 100 12 L 96 28 L 104 28 Z",                                                              // Crown finial
  "M 70 32 C 70 56, 130 56, 130 32 C 130 22, 110 18, 100 28 C 90 18, 70 22, 70 32 Z",          // Domed stopper
  "M 72 90 L 128 90 L 128 110 L 72 110 Z",                                                    // Collar
  "M 92 96 L 100 92 L 108 96 L 100 106 Z",                                                    // Diamond facet
  "M 80 110 L 120 110 L 130 144 L 70 144 Z",                                                  // Neck shoulder
  "M 70 144 C 36 160, 28 200, 52 232 L 148 232 C 172 200, 164 160, 130 144 Z",                // Upper body
  "M 52 232 L 148 232 L 152 268 L 48 268 Z",                                                  // Waist
  "M 48 268 C 30 290, 30 360, 50 410 L 150 410 C 170 360, 170 290, 152 268 Z",                // Lower body
  "M 50 410 L 150 410 L 156 460 L 44 460 Z",                                                  // Base shaft
  "M 44 460 L 156 460 L 158 480 L 42 480 Z",                                                  // Plinth
];

// Internal facets — vertical ridges + diagonal cross-lines suggest cut-crystal.
const FACETS = [
  "M 80 144 L 80 460",  "M 100 144 L 100 460", "M 120 144 L 120 460",
  "M 70 144 L 130 232", "M 130 144 L 70 232",
  "M 52 268 L 148 410", "M 148 268 L 52 410",
];

// Filigree swirl motif — concentrated at the cap, collar and plinth.
const FILIGREE_PATHS = [
  "M 78 50 C 70 50, 70 62, 80 62 C 90 62, 90 50, 100 50 C 110 50, 110 62, 120 62 C 130 62, 130 50, 122 50",
  "M 74 100 C 80 92, 90 108, 100 100 C 110 92, 120 108, 126 100",
  "M 38 320 C 30 320, 30 340, 46 340 C 62 340, 62 320, 78 320",
  "M 122 320 C 138 320, 138 340, 154 340 C 170 340, 170 320, 162 320",
  "M 50 472 C 60 462, 70 478, 80 470 C 90 462, 100 478, 110 470 C 120 462, 130 478, 140 470 C 150 462, 152 472, 150 472",
];

const PARTICLES = [
  { cx: 100, cy: 40 }, { cx: 100, cy: 100 },
  { cx: 50, cy: 340 }, { cx: 150, cy: 340 },
  { cx: 70, cy: 470 }, { cx: 100, cy: 472 }, { cx: 130, cy: 470 },
];

// --- Hoisted motion / style objects ----------------------------------------
// Pre-computed per-index transition objects. Since paths are pulled from
// fixed module-level arrays, each index's transition is stable for the entire
// lifetime of the module — no need to allocate a fresh object on every render.
const SILHOUETTE_TRANS = SILHOUETTE_PATHS.map((_, i) => ({
  duration: 0.7, delay: 0.6 + i * 0.07, ease: EASE,
}));
const FACET_TRANS = FACETS.map((_, i) => ({
  duration: 0.6, delay: 1.0 + i * 0.05, ease: EASE,
}));
const FILIGREE_TRANS = FILIGREE_PATHS.map((_, i) => ({
  duration: 0.9, delay: 1.4 + i * 0.12, ease: EASE,
}));
const PARTICLE_TRANS = PARTICLES.map((_, i) => ({
  duration: 0.4, delay: 1.8 + i * 0.05, ease: EASE,
}));

const STROKE_DRAW_INIT = { pathLength: 0, opacity: 0 };
const STROKE_DRAW_ANIM = { pathLength: 1, opacity: 1 };
const STROKE_DRAW = { initial: STROKE_DRAW_INIT, animate: STROKE_DRAW_ANIM };

const PARTICLE_INIT = { opacity: 0, scale: 0 };
const PARTICLE_ANIM = { opacity: 1, scale: 1 };

const OVERLAY_INIT = { opacity: 1 };
const OVERLAY_EXIT = { opacity: 0 };
const OVERLAY_TRANS = { duration: 0.9, ease: EASE };

const ACT1_TRANS = { duration: 0.6, ease: EASE };

const HANDOFF_STYLE = { filter: "blur(2px)", transition: "filter .9s ease" };
const CLEAR_STYLE = { filter: "none", transition: "filter .9s ease" };

function prefersReducedMotion() {
  if (typeof window === "undefined" || !window.matchMedia) return false;
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

// --- Sub-components --------------------------------------------------------
function SeedCurve() {
  return (
    <motion.path
      d="M 42 490 L 158 490"
      stroke={ROSE}
      strokeWidth="0.8"
      fill="none"
      {...STROKE_DRAW}
      transition={ACT1_TRANS}
    />
  );
}

function Silhouette() {
  return SILHOUETTE_PATHS.map((d, i) => (
    <motion.path
      key={`silhouette-${i}-${d.slice(0, 10)}`}
      d={d}
      stroke={ROSE}
      strokeWidth="0.6"
      fill="none"
      {...STROKE_DRAW}
      transition={SILHOUETTE_TRANS[i]}
    />
  ));
}

function FacetMesh() {
  return FACETS.map((d, i) => (
    <motion.path
      key={`facet-${i}-${d.slice(0, 10)}`}
      d={d}
      stroke={ROSE_FAINT}
      strokeWidth="0.4"
      fill="none"
      {...STROKE_DRAW}
      transition={FACET_TRANS[i]}
    />
  ));
}

function FiligreeBloom() {
  return FILIGREE_PATHS.map((d, i) => (
    <motion.path
      key={`filigree-${i}-${d.slice(0, 10)}`}
      d={d}
      stroke={ROSE}
      strokeWidth="0.7"
      fill="none"
      strokeLinecap="round"
      {...STROKE_DRAW}
      transition={FILIGREE_TRANS[i]}
    />
  ));
}

function Particles() {
  return PARTICLES.map((p, i) => (
    <motion.circle
      key={`particle-${p.cx}-${p.cy}`}
      cx={p.cx}
      cy={p.cy}
      r="1.2"
      fill={ROSE}
      initial={PARTICLE_INIT}
      animate={PARTICLE_ANIM}
      transition={PARTICLE_TRANS[i]}
    />
  ));
}

// Hook owns the 4-act phase scheduler. Cleans up on unmount or early hide.
function useDeconstructionPhases(active) {
  const [state, setState] = useState({ phase: "seed", show: active });

  useEffect(() => {
    if (!active) {
      setState((s) => ({ ...s, show: false }));
      return undefined;
    }
    const t1 = setTimeout(() => setState((s) => ({ ...s, phase: "lattice" })), 600);
    const t2 = setTimeout(() => setState((s) => ({ ...s, phase: "bloom" })), 1400);
    const t3 = setTimeout(() => setState((s) => ({ ...s, phase: "handoff" })), 2400);
    const t4 = setTimeout(() => setState((s) => ({ ...s, show: false })), SEQUENCE_MS);
    return () => [t1, t2, t3, t4].forEach(clearTimeout);
  }, [active]);

  return state;
}

export default function BottleDeconstruction({ skipped = false }) {
  // Reduced-motion users + returning visitors (`skipped`) bypass the animation
  // entirely. The initial value is locked at mount; subsequent prop changes
  // collapse the sequence via the `skipped` guard on `visible` below.
  const [active] = useState(() => !skipped && !prefersReducedMotion());
  const { show, phase } = useDeconstructionPhases(active);
  const visible = show && !skipped;

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          data-testid="bottle-deconstruction"
          aria-hidden
          className="absolute inset-0 pointer-events-none flex items-center justify-center z-[5]"
          initial={OVERLAY_INIT}
          exit={OVERLAY_EXIT}
          transition={OVERLAY_TRANS}
        >
          <motion.svg
            viewBox="0 0 200 600"
            preserveAspectRatio="xMidYMid meet"
            className="w-full max-w-[440px] h-auto"
            style={phase === "handoff" ? HANDOFF_STYLE : CLEAR_STYLE}
          >
            <SeedCurve />
            <Silhouette />
            <FacetMesh />
            <FiligreeBloom />
            <Particles />
          </motion.svg>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
